import React, { useState, useEffect, useRef } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import UploadImg from "../../assets/images/icons/upload-img.svg";
import { useNavigate } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { UI_CONSTANTS } from "../../utils/constants";
import { DocumentUploadCard } from "../../commons/DocumentCard/DocumentCard";

const TEXT_FIELDS = [
  "full_name",
  "email",
  "country_code",
  "phone",
  "dob",
  "full_address",
  "house_no",
  "pincode",
  "landmark",
  "state",
  "country",
  "license_number",
  "vehicle_number",
];

const FILE_FIELDS = ["image", "work_permit", "government_id", "driving_license"];

const TEXT_FIELD_MAX_LENGTH = {
  full_name: 50,
  email: 100,
  country: 50,
  state: 50,
  landmark: 200,
  full_address: 200,
  country_code: 4,
  phone: 10,
  pincode: 6,
  license_number: 16,
  vehicle_number: 11,
};

const FILE_FIELD_LABELS = {
  image: "profile photo",
  work_permit: "work permit document",
  government_id: "government issued id document",
  driving_license: "driving license document",
};

const NUMERIC_FIELDS = new Set(["country_code", "phone", "house_no", "pincode"]);

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const isValidEmail = (value) => EMAIL_PATTERN.test(String(value || "").trim());

const getEmailValidationError = (value) => {
  const email = String(value || "").trim();

  if (!email) {
    return "Please enter email address";
  }
  if (email.length > TEXT_FIELD_MAX_LENGTH.email) {
    return "Email must be 100 characters or less";
  }
  if (!isValidEmail(email)) {
    return "Please enter a valid email address";
  }

  return "";
};

const sanitizeDriverFieldValue = (name, value) => {
  let nextValue = value;

  if (NUMERIC_FIELDS.has(name)) {
    nextValue = nextValue.replace(/\D/g, "");
  } else if (name === "email") {
    nextValue = nextValue.replace(/\s/g, "");
  } else if (name === "license_number" || name === "vehicle_number") {
    nextValue = nextValue.replace(/[^a-zA-Z0-9]/g, "");
  } else {
    nextValue = nextValue.replace(/^\s+/, "");
  }

  if (TEXT_FIELD_MAX_LENGTH[name]) {
    nextValue = nextValue.slice(0, TEXT_FIELD_MAX_LENGTH[name]);
  }

  return nextValue;
};

const validateDriverForm = (formData) => {
  if (!formData.full_name?.trim()) {
    return "Please enter driver name";
  }
  if (formData.full_name.trim().length > TEXT_FIELD_MAX_LENGTH.full_name) {
    return "Name must be 50 characters or less";
  }

  const emailValidationError = getEmailValidationError(formData.email);
  if (emailValidationError) {
    return emailValidationError;
  }

  if (!formData.country_code?.trim()) {
    return "Please enter country code";
  }
  if (!/^\d+$/.test(formData.country_code)) {
    return "Country code must contain numbers only";
  }

  if (!formData.phone?.trim()) {
    return "Please enter mobile number";
  }
  if (!/^\d{10}$/.test(formData.phone)) {
    return "Mobile number must be exactly 10 digits";
  }

  if (!formData.dob) {
    return "Please select date of birth";
  }

  if (!formData.country?.trim()) {
    return "Please enter country";
  }
  if (formData.country.trim().length > TEXT_FIELD_MAX_LENGTH.country) {
    return "Country must be 50 characters or less";
  }

  if (!formData.house_no?.trim()) {
    return "Please enter house/flat number";
  }
  if (!/^\d+$/.test(formData.house_no)) {
    return "House/flat number must contain numbers only";
  }

  if (!formData.landmark?.trim()) {
    return "Please enter landmark";
  }
  if (formData.landmark.trim().length > TEXT_FIELD_MAX_LENGTH.landmark) {
    return "Landmark must be 200 characters or less";
  }

  if (!formData.state?.trim()) {
    return "Please enter state";
  }
  if (formData.state.trim().length > TEXT_FIELD_MAX_LENGTH.state) {
    return "State must be 50 characters or less";
  }

  if (!formData.pincode?.trim()) {
    return "Please enter pincode";
  }
  if (!/^\d{6}$/.test(formData.pincode)) {
    return "Pincode must be exactly 6 digits";
  }

  if (!formData.full_address?.trim()) {
    return "Please enter full address";
  }
  if (formData.full_address.trim().length > TEXT_FIELD_MAX_LENGTH.full_address) {
    return "Full address must be 200 characters or less";
  }

  if (!formData.license_number?.trim()) {
    return "Please enter license number";
  }
  if (!/^[a-zA-Z0-9]{1,16}$/.test(formData.license_number)) {
    return "License number must be alphanumeric with a maximum of 16 characters";
  }

  if (!formData.vehicle_number?.trim()) {
    return "Please enter vehicle registration number";
  }
  if (!/^[a-zA-Z0-9]{1,11}$/.test(formData.vehicle_number)) {
    return "Vehicle registration number must be alphanumeric with a maximum of 11 characters";
  }

  if (!formData.latitude?.trim() || !formData.longitude?.trim()) {
    return "Unable to fetch location. Please enable location access and try again";
  }
  if (Number.isNaN(Number(formData.latitude)) || Number.isNaN(Number(formData.longitude))) {
    return "Invalid location coordinates";
  }

  for (const field of FILE_FIELDS) {
    if (!formData[field]) {
      return `Please upload ${FILE_FIELD_LABELS[field]}`;
    }
  }

  return null;
};

const DRIVER_UPLOAD_DOCUMENTS = [
  { field: "government_id", title: "Government Issued Id", label: "Government Issued Id Document" },
  { field: "driving_license", title: "Driver License", label: "Driver License Document" },
  { field: "work_permit", title: "Work Permit", label: "Work Permit Document" },
];

function AddDriver() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    country_code: "91",
    phone: "",
    dob: "",
    full_address: "",
    house_no: "",
    pincode: "",
    landmark: "",
    state: "",
    country: "",
    license_number: "",
    vehicle_number: "",
    latitude: "",
    longitude: "",
    image: null,
    work_permit: null,
    government_id: null,
    driving_license: null,
  });
  const [previews, setPreviews] = useState({
    image: null,
    work_permit: null,
    government_id: null,
    driving_license: null,
  });
  const [dobInputType, setDobInputType] = useState("text");
  const [emailError, setEmailError] = useState("");
  const dobInputRef = useRef(null);
  const previewsRef = useRef(previews);
  previewsRef.current = previews;

  const openDobPicker = () => {
    setDobInputType("date");
    requestAnimationFrame(() => {
      const input = dobInputRef.current;
      if (!input) return;
      input.focus();
      if (typeof input.showPicker === "function") {
        try {
          input.showPicker();
        } catch {
          // Browser may block showPicker if not from a direct user gesture
        }
      }
    });
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData((prev) => ({
            ...prev,
            latitude: String(position.coords.latitude),
            longitude: String(position.coords.longitude),
          }));
        },
        (error) => {
          console.error("Location error:", error);
        }
      );
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizeDriverFieldValue(name, value),
    }));
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
    }
  };

  const handleEmailBeforeInput = (e) => {
    if (e.data && /\s/.test(e.data)) {
      e.preventDefault();
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value.replace(/\s/g, "").slice(0, TEXT_FIELD_MAX_LENGTH.email);
    setFormData((prev) => ({
      ...prev,
      email: value,
    }));
    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    if (!formData.email) {
      setEmailError("");
      return;
    }

    setEmailError(getEmailValidationError(formData.email));
  };

  const handleEmailPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/\s/g, "");
    const input = e.target;
    const start = input.selectionStart ?? formData.email.length;
    const end = input.selectionEnd ?? formData.email.length;
    const nextValue = `${formData.email.slice(0, start)}${pastedText}${formData.email.slice(end)}`
      .replace(/\s/g, "")
      .slice(0, TEXT_FIELD_MAX_LENGTH.email);

    setFormData((prev) => ({
      ...prev,
      email: nextValue,
    }));
    if (emailError) {
      setEmailError("");
    }
  };

  const revokePreviewUrl = (url) => {
    if (url && typeof url === "string" && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(previewsRef.current).forEach(revokePreviewUrl);
    };
  }, []);

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files?.[0]) return;

    const file = files[0];
    setFormData((prev) => ({
      ...prev,
      [name]: file,
    }));

    setPreviews((prev) => {
      revokePreviewUrl(prev[name]);
      return {
        ...prev,
        [name]: URL.createObjectURL(file),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateDriverForm(formData);
    if (validationError) {
      const currentEmailError = getEmailValidationError(formData.email);
      if (validationError === currentEmailError) {
        setEmailError(currentEmailError);
      }
      toast.error(validationError);
      return;
    }
    setEmailError("");

    try {
      setLoading(true);
      const submitData = new FormData();

      TEXT_FIELDS.forEach((key) => {
        submitData.append(key, formData[key]);
      });

      submitData.append("latitude", formData.latitude);
      submitData.append("longitude", formData.longitude);
      submitData.append("driver_category", "1");

      FILE_FIELDS.forEach((key) => {
        submitData.append(key, formData[key]);
      });

      const response = await handleApiCall("post", endpoints.addDriver, submitData);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(response?.data?.message || "Driver added successfully");
        setTimeout(() => {
          navigate("/driver-managment");
        }, 1000);
      }
    } catch (error) {
      console.error("Add driver error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Add Driver</h3>
            </div>

            <div className="mb-4 px-0 py-2">
              <form onSubmit={handleSubmit}>
                <div className="card-heading d-flex align-items-center mb-3 pb-2">
                  <div className="d-flex align-items-center justify-content-start col-md-12 px-0">
                    <Button type="submit" className="rounded-lg" disabled={loading}>
                      {loading ? "Creating..." : "Add"}
                    </Button>
                    <Button
                      type="button"
                      className="rounded-lg ml-4 bg-white"
                      onClick={() => navigate("/driver-managment")}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
                <div className="col-md-12 tab-content row px-0 mx-0">
                  <label className="imageUpload" role="button">
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      name="image"
                      onChange={handleFileChange}
                    />
                    {previews.image ? (
                      <img
                        src={previews.image}
                        alt="Driver"
                        style={{ width: "100px", height: "100px", objectFit: "cover" }}
                      />
                    ) : (
                      <>
                        <img src={UploadImg} className="icon" alt="" />
                        <div className="text-white mt-1">Upload photo</div>
                      </>
                    )}
                  </label>
                </div>
                <h5 className="text-white mt-4 mb-3">DRIVER DETAILS</h5>
                <div className="row mx-n2">
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Name*"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      maxLength={TEXT_FIELD_MAX_LENGTH.full_name}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className={`form-control default ${emailError ? "is-invalid" : ""}`}
                      placeholder="Enter Email Address*"
                      name="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      onKeyDown={handleEmailKeyDown}
                      onBeforeInput={handleEmailBeforeInput}
                      onPaste={handleEmailPaste}
                      inputMode="email"
                      autoComplete="email"
                      maxLength={TEXT_FIELD_MAX_LENGTH.email}
                      required
                    />
                    {emailError && <div className="invalid-feedback d-block">{emailError}</div>}
                  </div>
                  <div className="form-group col-md-2 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Code*"
                      name="country_code"
                      value={formData.country_code}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      maxLength={TEXT_FIELD_MAX_LENGTH.country_code}
                      required
                    />
                  </div>
                  <div className="form-group col-md-2 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Mobile number*"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      maxLength={TEXT_FIELD_MAX_LENGTH.phone}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Enter country*"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      maxLength={TEXT_FIELD_MAX_LENGTH.country}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      ref={dobInputRef}
                      type={dobInputType}
                      className="form-control default"
                      placeholder="D.O.B*"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      onClick={openDobPicker}
                      onFocus={openDobPicker}
                      onBlur={(e) => {
                        if (!e.target.value) {
                          setDobInputType("text");
                        }
                      }}
                      style={{ cursor: "pointer" }}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="House/ Flat No*"
                      name="house_no"
                      value={formData.house_no}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Landmark*"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                      maxLength={TEXT_FIELD_MAX_LENGTH.landmark}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="State*"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      maxLength={TEXT_FIELD_MAX_LENGTH.state}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Pincode*"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      maxLength={TEXT_FIELD_MAX_LENGTH.pincode}
                      required
                    />
                  </div>
                </div>
                <div className="form-group col-md-8 px-2">
                  <input
                    type="text"
                    className="form-control default"
                    placeholder="Enter Full Address*"
                    name="full_address"
                    value={formData.full_address}
                    onChange={handleInputChange}
                    maxLength={TEXT_FIELD_MAX_LENGTH.full_address}
                    required
                  />
                </div>

                <h5 className="text-white mt-4 mb-3">VEHICLE DETAILS</h5>
                <div className="row mx-n2">
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="License Number*"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleInputChange}
                      maxLength={TEXT_FIELD_MAX_LENGTH.license_number}
                      required
                    />
                  </div>
                  <div className="form-group col-md-4 px-2">
                    <input
                      type="text"
                      className="form-control default"
                      placeholder="Vehicle Registration*"
                      name="vehicle_number"
                      value={formData.vehicle_number}
                      onChange={handleInputChange}
                      maxLength={TEXT_FIELD_MAX_LENGTH.vehicle_number}
                      required
                    />
                  </div>
                </div>

                <h5 className="text-white mt-4 mb-3">DOCUMENTS</h5>
                <div className="row mx-n2">
                  {DRIVER_UPLOAD_DOCUMENTS.map((doc) => (
                    <DocumentUploadCard
                      key={doc.field}
                      title={doc.title}
                      documentLabel={doc.label}
                      name={doc.field}
                      previewUrl={previews[doc.field]}
                      hasFile={Boolean(formData[doc.field])}
                      onFileChange={handleFileChange}
                    />
                  ))}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddDriver;
